import { ovReq } from "@/api/ov-req";
import { formatPayout } from "@/lib/numbers";
import { compareFavoriteFirst } from "@/utils/publisherFavorite";
import { getFlaggedReasonLabel } from "@/utils/flagReasons";
import { getMediaTypeBadge } from "@/utils/mediaType";
import { withAutoUnflagTime } from "@/utils/placementFlag";
import { formatRewardedMarketplaceCpiImpactMessage } from "@/utils/rewardedMarketplaceCpiImpactMessage";

export function isMarketplacePublisher(publisher) {
	return publisher?.publisher_type === "marketplace";
}

export function isApprovedPublisher(publisher) {
	if (!isMarketplacePublisher(publisher)) {
		return true;
	}
	return publisher.publisher_marketplace_status === "approved";
}

export function isConnectedPublisher(publisher) {
	return Boolean(
		publisher.create_placement || (publisher.id !== null && publisher.status === "live"),
	);
}

export function isPendingConnection(publisher) {
	return Boolean(
		publisher.create_placement && (publisher.id === null || publisher.status !== "live"),
	);
}

export function isFlaggedPublisher(publisher) {
	return Boolean(publisher.is_flagged);
}

export function flaggedTooltip(publisher) {
	return withAutoUnflagTime(
		getFlaggedReasonLabel(
			publisher?.flagged_reason,
			"This placement has been flagged",
		),
		publisher?.flagged_until,
	);
}

export function publisherStatusKind(publisher) {
	if (isFlaggedPublisher(publisher)) {
		return "flagged";
	}
	if (isPendingConnection(publisher)) {
		return "pending";
	}
	if (isConnectedPublisher(publisher)) {
		return "connected";
	}
	if (!isMarketplacePublisher(publisher)) {
		return null;
	}
	return isApprovedPublisher(publisher) ? "approved" : "not-approved";
}

export function publisherRowClass(publisher) {
	const connected = isConnectedPublisher(publisher);
	const approved = isApprovedPublisher(publisher);
	return {
		"not-approved": !approved,
		"row-connected": connected,
		"row-unconnected": approved && !connected,
		"row-flagged": isFlaggedPublisher(publisher),
	};
}

export function publisherInitial(publisher) {
	return (publisher.publisher_name || "?").trim().charAt(0).toUpperCase() || "?";
}

export function getPayoutModelLabel(publisher, offer) {
	if (!isConnectedPublisher(publisher)) {
		if (!isMarketplacePublisher(publisher)) {
			return "—";
		}
		if (publisher.calculated_payout_type) {
			return publisher.calculated_payout_type;
		}
		if (publisher.publisher_media_type === "rewarded") {
			return "CPI";
		}
		return offer?.payout_type || "CPI";
	}

	return publisher.payout_type
		|| publisher.calculated_payout_type
		|| offer?.payout_type
		|| "CPI";
}

function formatPayoutAmount(mode, payout, currency) {
	const value = Number(payout);
	if (!Number.isFinite(value) || value <= 0) {
		return "—";
	}
	if (mode === "dynamic") {
		return `${value}%`;
	}
	const amount = formatPayout(value);
	return !currency || currency === "USD" ? `$${amount}` : `${amount} ${currency}`;
}

/** Fallback label when marketplace payout is revenue-share (not a fixed amount). */
export const MARKETPLACE_REVENUE_SHARE_LABEL = "40% of revenue";

export function getPayoutLabel(publisher, offer) {
	if (isRewardedMarketplacePublisherRow(publisher)) {
		return formatRewardedMarketplacePriceSummary(getRewardedMarketplacePrices(publisher));
	}

	if (!isConnectedPublisher(publisher)) {
		if (!isMarketplacePublisher(publisher)) {
			return "—";
		}
		const mode = publisher.calculated_payout_mode || "fixed";
		const payout = mode === "dynamic"
			? (publisher.calculated_payout_percent ?? 40)
			: publisher.calculated_payout;
		return formatPayoutAmount(mode, payout, publisher.currency || offer?.currency);
	}

	return formatPayoutAmount(
		publisher.payout_mode || "fixed",
		publisher.payout,
		publisher.currency || offer?.currency,
	);
}

export function getMarketplacePayoutLabel(publisher, offer) {
	return getPayoutLabel(publisher, offer);
}

export function formatCapUsage(currentValue, capValue, useShortNumber = false) {
	if (currentValue == null && capValue == null) {
		return "—";
	}

	const current = Number(currentValue || 0);
	const cap = Number(capValue || 0);
	const currentLabel = useShortNumber ? shortNumber(current) : numberCommas(current);
	const capLabel = cap > 0
		? (useShortNumber ? shortNumber(cap) : numberCommas(cap))
		: "Open";

	return `${currentLabel} / ${capLabel}`;
}

export function mapMarketplacePublisher(publisher) {
	return {
		...publisher,
		create_placement: publisher.id !== null && publisher.status === "live",
		disabled: !!publisher.auto_generated,
	};
}

/**
 * Loads marketplace publisher connection state for an offer.
 * @param {object} offer
 * @returns {Promise<object[]>}
 */
export async function fetchMarketplaceOfferConnections(offer) {
	if (!offer?.id) {
		return [];
	}
	try {
		const resp = await ovReq.get(`placement/getMarketplaceOfferStatus/${offer.id}`);
		return (resp.placement_status_list || []).map(mapMarketplacePublisher);
	} catch (e) {
		console.error(e);
		return [];
	}
}

export function isRewardedMarketplacePublisherRow(publisher) {
	return publisher?.publisher_type === "marketplace"
		&& publisher?.publisher_media_type === "rewarded";
}

export function getRewardedMarketplacePrices(publisher) {
	return publisher?.rewarded_marketplace_prices || [];
}

export function getRewardedMarketplaceUnsupported(publisher) {
	return publisher?.rewarded_marketplace_coverage?.unsupported || [];
}

export function getRewardedMarketplaceSupported(publisher) {
	return publisher?.rewarded_marketplace_coverage?.supported || [];
}

export function isRewardedMarketplaceConnectable(publisher) {
	if (!isRewardedMarketplacePublisherRow(publisher)) {
		return true;
	}
	if (typeof publisher.rewarded_marketplace_connectable === "boolean") {
		return publisher.rewarded_marketplace_connectable;
	}
	return getRewardedMarketplacePrices(publisher).some(
		(pair) => getRewardedMarketplaceFinalPrice(pair) != null,
	);
}

export const REWARDED_MARKETPLACE_UNSUPPORTED_TRAFFIC_TOOLTIP =
	"This publisher does not support this kind of traffic";

/** Why the Connect button is disabled for a marketplace rewarded row. Empty if connectable. */
export function getRewardedMarketplaceConnectDisabledReason(publisher) {
	if (publisher?.create_placement) {
		return "";
	}
	if (
		isRewardedMarketplacePublisherRow(publisher)
		&& !isRewardedMarketplaceConnectable(publisher)
	) {
		return REWARDED_MARKETPLACE_UNSUPPORTED_TRAFFIC_TOOLTIP;
	}
	return "";
}

function formatPriceAmount(value) {
	if (value == null || !Number.isFinite(Number(value))) {
		return null;
	}
	return `$${formatPayout(value)}`;
}

/** Final CPI for a Country×OS pair (effective tier only — no base/geo/os breakdown). */
export function getRewardedMarketplaceFinalPrice(pair) {
	if (!pair) {
		return null;
	}
	const value = pair.effective_price ?? pair.price;
	return value != null && Number.isFinite(Number(value)) ? Number(value) : null;
}

export function formatRewardedMarketplacePricePair(pair) {
	const label = `${pair.country}/${pair.os}`;
	const effective = formatPriceAmount(getRewardedMarketplaceFinalPrice(pair));
	return effective ? `${label} ${effective}` : `${label} —`;
}

function pricedRewardedMarketplacePairs(pairs) {
	return (pairs || []).filter((pair) => getRewardedMarketplaceFinalPrice(pair) != null);
}

/** Priced Country×OS rows only (final CPI available). */
export function getRewardedMarketplacePricedPrices(publisher) {
	return pricedRewardedMarketplacePairs(getRewardedMarketplacePrices(publisher));
}

/** Single-line label for dense tables: first priced pair plus a count of the rest. */
export function formatRewardedMarketplacePriceSummary(pairs) {
	const priced = pricedRewardedMarketplacePairs(pairs);
	if (!priced.length) {
		return "—";
	}
	const first = formatRewardedMarketplacePricePair(priced[0]);
	return priced.length > 1 ? `${first} +${priced.length - 1}` : first;
}

/** Full list for tooltips / locked payout display — one final Country/OS CPI per line. */
export function formatRewardedMarketplacePriceList(pairs) {
	const priced = pricedRewardedMarketplacePairs(pairs);
	if (!priced.length) {
		return "";
	}
	return priced.map((pair) => formatRewardedMarketplacePricePair(pair)).join("\n");
}

function formatCountryOsLabel(pair) {
	const country = pair?.country || "?";
	const os = pair?.os ? String(pair.os) : "?";
	return `${country} · ${os}`;
}

function formatCountryOsList(pairs) {
	return (pairs || []).map(formatCountryOsLabel).join("\n");
}

/** Confirm body when connecting a rewarded marketplace publisher with partial Country×OS coverage. */
export function formatRewardedMarketplacePartialSupportMessage(publisher) {
	const unsupported = getRewardedMarketplaceUnsupported(publisher);
	if (!unsupported.length) {
		return "";
	}

	const supported = getRewardedMarketplaceSupported(publisher);
	const lines = [
		"This publisher can only run some of this offer's countries / OS.",
		"",
	];

	if (supported.length) {
		lines.push("Will run:");
		lines.push(formatCountryOsList(supported));
		lines.push("");
	}

	lines.push("Won't run (not supported):");
	lines.push(formatCountryOsList(unsupported));
	lines.push("");
	lines.push("You can still connect — unsupported traffic will not arrive.");

	return lines.join("\n");
}

/** Show the CPI-change confirm modal; returns false if the user cancels. */
export async function showRewardedMarketplaceCpiImpactConfirm(vm, impact) {
	const modalApi = vm.$modal || vm.$bvModal;
	if (!modalApi?.show) {
		// Fallback for contexts without the app modal plugin.
		return modalApi?.msgBoxConfirm?.(
			formatRewardedMarketplaceCpiImpactMessage(impact),
			{
				title: "Price will change for rewarded publishers",
				okTitle: "Save and update CPI",
				cancelTitle: "Cancel",
				centered: true,
				width: "560px",
			},
		);
	}

	const { default: RewardedMarketplaceCpiImpactConfirmModal } = await import(
		"@/views/modals/RewardedMarketplaceCpiImpactConfirmModal.vue"
	);

	return new Promise((resolve) => {
		let settled = false;
		const finish = (result) => {
			if (settled) {
				return;
			}
			settled = true;
			resolve(!!result);
		};

		modalApi.show(
			RewardedMarketplaceCpiImpactConfirmModal,
			{
				impact,
				onConfirm: () => finish(true),
				onCancel: () => finish(false),
			},
			{
				width: "min(640px, 96%)",
				height: "auto",
				scrollable: true,
				clickToClose: false,
			},
			{
				beforeClose: () => finish(false),
			},
		);
	});
}

/**
 * Preview CPI impact for connected rewarded pubs; confirm if any Country×OS CPI changes.
 * @param {object} vm Vue instance with $ovReq and $bvModal
 * @param {object} data offer/save payload
 * @returns {Promise<boolean>} false if user cancels
 */
export async function confirmRewardedMarketplaceCpiChangeIfNeeded(vm, data) {
	if (!data?.id) {
		return true;
	}

	let impact;
	try {
		impact = await vm.$ovReq.post("offer/previewRewardedMarketplaceCpiImpact", data);
	} catch (e) {
		console.error(e);
		return false;
	}

	if (!impact?.has_changes) {
		return true;
	}

	const confirmed = await showRewardedMarketplaceCpiImpactConfirm(vm, impact);
	if (!confirmed) {
		return false;
	}

	data.confirm_rewarded_marketplace_cpi_change = true;
	return true;
}

/**
 * Preview the CPI impact of an existing rewarded marketplace placement save.
 * Mirrors the offer-save preflight so the normal save request is not used to
 * trigger the confirmation modal.
 */
export async function confirmRewardedMarketplacePlacementCpiChangeIfNeeded(vm, data) {
	if (!data?.id) {
		return true;
	}

	let impact;
	try {
		impact = await vm.$ovReq.post("placement/previewRewardedMarketplaceCpiImpact", data);
	} catch (e) {
		console.error(e);
		return false;
	}

	if (!impact?.has_changes) {
		return true;
	}

	const confirmed = await showRewardedMarketplaceCpiImpactConfirm(vm, impact);
	if (!confirmed) {
		return false;
	}

	data.confirm_rewarded_marketplace_cpi_change = true;
	return true;
}

/**
 * Handle a save rejection that requires CPI confirmation (server re-check).
 * @returns {Promise<boolean>} false if user cancels or error is unrelated
 */
export async function confirmRewardedMarketplaceCpiChangeFromSaveError(vm, data, err) {
	const payload = err?.data || err;
	if (payload?.code !== "rewarded_marketplace_cpi_change" || !payload?.impact?.has_changes) {
		return false;
	}

	const confirmed = await showRewardedMarketplaceCpiImpactConfirm(vm, payload.impact);
	if (!confirmed) {
		return false;
	}

	data.confirm_rewarded_marketplace_cpi_change = true;
	return true;
}

export function combinePublishers(placementStatusList, marketplacePublishers, showMarketplace) {
	const publishers = (placementStatusList || []).filter(
		(publisher) => publisher.publisher_type !== "marketplace",
	);
	if (showMarketplace) {
		publishers.push(...marketplacePublishers);
	}
	return publishers;
}

export function filterActivePublishers(publishers, activeOnly) {
	if (!activeOnly) {
		return publishers;
	}
	return publishers.filter((publisher) => {
		if (isMarketplacePublisher(publisher) && !isApprovedPublisher(publisher)) {
			return true;
		}
		return !!publisher.publisher_status;
	});
}

export function buildSupplyTabs(publishers) {
	const mediaTypeMap = publishers.reduce((hash, publisher) => {
		if (!publisher.publisher_media_type) {
			return hash;
		}
		const existing = hash[publisher.publisher_media_type];
		hash[publisher.publisher_media_type] = {
			key: publisher.publisher_media_type,
			label: getMediaTypeBadge(publisher.publisher_media_type).label,
			count: (existing?.count || 0) + 1,
		};
		return hash;
	}, {});

	return [
		{ key: "all", label: "All", count: publishers.length },
		...Object.keys(mediaTypeMap).sort().map((key) => mediaTypeMap[key]),
		{
			key: "connected",
			label: "Connected",
			count: publishers.filter(isConnectedPublisher).length,
		},
	];
}

export function filterAndSortPublishers(publishers, { selectedTab, searchQuery }) {
	let list = publishers;

	if (selectedTab === "connected") {
		list = list.filter(isConnectedPublisher);
	} else if (selectedTab !== "all") {
		list = list.filter((publisher) => publisher.publisher_media_type === selectedTab);
	}

	const query = searchQuery.trim().toLowerCase();
	if (query) {
		list = list.filter((publisher) => {
			const name = (publisher.publisher_name || "").toLowerCase();
			return name.includes(query) || String(publisher.publisher_id).includes(query);
		});
	}

	return list.slice().sort((a, b) => {
		const connectedDelta = Number(isConnectedPublisher(b)) - Number(isConnectedPublisher(a));
		if (connectedDelta) {
			return connectedDelta;
		}
		const favoriteDelta = compareFavoriteFirst(a.publisher_is_favorite, b.publisher_is_favorite);
		if (favoriteDelta) {
			return favoriteDelta;
		}
		const marketplaceDelta = Number(isMarketplacePublisher(b)) - Number(isMarketplacePublisher(a));
		if (marketplaceDelta) {
			return marketplaceDelta;
		}
		return (a.publisher_name || "").localeCompare(b.publisher_name || "");
	});
}

/**
 * Applies connectPublisher API placement onto a Supply row.
 * Mutates `publisher` in place (shared with parent placement/marketplace lists).
 * @param {object} publisher
 * @param {object | null | undefined} placement
 */
export function applySavedPlacement(publisher, placement) {
	if (!placement) {
		publisher.create_placement = false;
		publisher.id = null;
		publisher.status = null;
		delete publisher.placement_payout_overrides;
		return;
	}

	publisher.id = placement.id;
	publisher.status = placement.status;
	publisher.create_placement = placement.status === "live";
	publisher.payout = placement.payout;
	publisher.payout_type = placement.payout_type;
	publisher.payout_mode = placement.payout_mode;
	publisher.currency = placement.currency;
	if (placement.payable_events) {
		publisher.payable_events = placement.payable_events;
	}
	delete publisher.placement_payout_overrides;
}

// Connect/Disconnect Publisher - create/live/pause placement
export async function savePublisherConnection({ offerId, publisherId, connect, overrides }) {
	const body = {
		offer_id: offerId,
		publisher_id: publisherId,
		connect: !!connect,
	};
	if (overrides) {
		body.overrides = overrides;
	}
	return ovReq.post("placement/connectPublisher", body);
}

function numberCommas(value) {
	return Number(value || 0).toLocaleString();
}

function shortNumber(value) {
	const num = Number(value || 0);
	if (num >= 1_000_000) {
		return `${(num / 1_000_000).toFixed(1)}M`;
	}
	if (num >= 1_000) {
		return `${(num / 1_000).toFixed(1)}K`;
	}
	return String(num);
}
